import {
  Caption,
  StyleConfig,
  AnimationKind,
  BackgroundAnimationKind,
} from '@contentlab/types/captions';
import {getCurrentCaption, divideCaption} from './CaptionProcessor';
import applyCapitalizationToCaption from './TextCapitalization';
import {wrapTextRegular, wrapTextWordByWord} from './TextWrapper';
import {getTimedWords} from './WordTimingProcessor';
import {WrappedTextResult, HighlightTextResult} from './types';

export const getCurrentLines = (
  context: CanvasRenderingContext2D,
  captions: Caption[],
  style: StyleConfig,
  maxWidth: number,
  currentTime: number
): WrappedTextResult => {
  let currentCaption = getCurrentCaption(captions, currentTime);
  if (!currentCaption) return {lines: [], start: 0, end: 0};

  const dividedCaptions = divideCaption({
    caption: currentCaption,
    context,
    maxWidth,
  });
  currentCaption = getCurrentCaption(dividedCaptions, currentTime);
  if (!currentCaption) return {lines: [], start: 0, end: 0};

  const capitalizedCaption = applyCapitalizationToCaption(
    currentCaption,
    style.typography.textCapitalization
  );

  return style.animation?.type === AnimationKind.WORD_BY_WORD
    ? wrapTextWordByWord(context, capitalizedCaption, maxWidth, currentTime)
    : wrapTextRegular(context, capitalizedCaption, maxWidth);
};

export const getHighlightText = (
  context: CanvasRenderingContext2D,
  captions: Caption[],
  style: StyleConfig,
  maxWidth: number,
  currentTime: number
): HighlightTextResult => {
  const currentCaption = getCurrentCaption(captions, currentTime);
  if (!currentCaption) return {lines: [], start: 0, end: 0, timedWords: []};

  const capitalizedCaption = applyCapitalizationToCaption(
    currentCaption,
    style.typography.textCapitalization
  );

  const dividedCaptions = divideCaption({
    caption: capitalizedCaption,
    context,
    maxWidth,
  });
  const processedCaption = getCurrentCaption(dividedCaptions, currentTime);
  if (!processedCaption) return {lines: [], start: 0, end: 0, timedWords: []};

  if (
    style.animation?.backgroundAnimationType === BackgroundAnimationKind.BOX
  ) {
    context.save();
    context.scale(
      style.animation?.wordMaxScaling ?? 1,
      style.animation?.wordMaxScaling ?? 1
    );
    const wrappedText = wrapTextRegular(context, processedCaption, maxWidth);
    context.restore();
    return {...wrappedText, timedWords: getTimedWords(processedCaption)};
  }

  const wrappedText = wrapTextRegular(context, processedCaption, maxWidth);
  return {...wrappedText, timedWords: getTimedWords(processedCaption)};
};
